import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  createTerritoryVisit,
  getTerritoryVisitContext
} from '../../API/apiService';
import { useUserName } from '../../contexts/userName';
import CustomFilterSelect from '../../shared/customFilterSelect';
import './style.scss';

const SESSION_TYPES = [
  { value: 'sesion_docentes', label: 'Sesión Docentes' },
  { value: 'sesion_estudiantes', label: 'Sesión Estudiantes' },
  { value: 'sesion_comunidad', label: 'Sesión Comunidad' },
  { value: 'evento_docentes', label: 'Evento Docentes' },
  { value: 'evento_estudiantes', label: 'Evento Estudiantes' },
  { value: 'evento_comunidad', label: 'Evento Comunidad' }
];

const METHODOLOGY_QUESTIONS = [
  {
    key: 'planned_execution',
    text: '1. ¿Se correspondió lo ejecutado con lo planeado en la guía pedagógica o bitácora?'
  },
  {
    key: 'objectives',
    text: '2. ¿Se orientó el encuentro de acuerdo con los objetivos de la guía pedagógica o bitácora?'
  },
  {
    key: 'support_materials',
    text: '3. ¿Se utilizaron materiales de apoyo adecuados para las actividades propuestas?'
  },
  {
    key: 'duration',
    text: '4. ¿La duración de la actividad posibilitó el cumplimiento del objetivo?'
  }
];

const FACILITATOR_CAPACITIES = [
  {
    key: 'critical_thinking',
    title: 'PENSAMIENTO CRÍTICO',
    text: 'Motiva el análisis crítico de la realidad. Invita a la argumentación razonada y basada en análisis. Promueve la reflexión desde diversos puntos de vista. Promueve la reflexión desde la experiencia vivida.'
  },
  {
    key: 'affiliation',
    title: 'AFILIACIÓN',
    text: 'Motiva el trabajo colaborativo entre los participantes y de estos con su entorno. Propicia el reconocimiento y valoración en la diferencia y promoción de la equidad. Propicia espacios, reflexiones y acciones favorables a la inclusión.'
  },
  {
    key: 'ethical_praxis',
    title: 'PRÁXIS ÉTICA',
    text: 'Reconoce, promueve y respeta los acuerdos sociales y los mínimos éticos. Promueve la toma de decisiones argumentadas, racionales y autónomas. Promueve la reflexión acerca de la incidencia de las decisiones individuales sobre los otros y lo otro.'
  },
  {
    key: 'environment_control',
    title: 'CONTROL DEL PROPIO ENTORNO',
    text: 'Invita a participar en las decisiones que afectan su ser y su entorno. Promueve la toma de decisiones democráticas en los asuntos colectivos de su entorno. Promueve la comprensión de la condición de agente de desarrollo y corresponsable en la transformación de sus realidades.'
  },
  {
    key: 'imagination',
    title: 'IMAGINACIÓN',
    text: 'Promueve la reflexión apoyado en la utilización de recursos artísticos, literarios y visuales. Aprovecha técnicas interactivas para incentivar la construcción creativa sobre la propia realidad. Incorpora narrativas aspiracionales sobre el proyecto de vida.'
  }
];

const PARTICIPANT_CAPACITIES = [
  {
    key: 'critical_thinking',
    title: 'PENSAMIENTO CRÍTICO',
    text: 'Manifiestan interés o conocimiento de la realidad de su contexto. Presentan argumentaciones razonadas e informadas. Reconocen y respetan la existencia de otros puntos de vista.'
  },
  {
    key: 'affiliation',
    title: 'AFILIACIÓN',
    text: 'Manifiestan interés por el trabajo colaborativo. Tratan con respeto al otro independientemente de las diferencias. Tienen expresiones favorables a la inclusión y la equidad.'
  },
  {
    key: 'ethical_praxis',
    title: 'PRÁXIS ÉTICA',
    text: 'Establecen y respetan los acuerdos grupales. Expresan respeto por normas y valores éticos desde la palabra y la acción. Reflexionan acerca de la responsabilidad sobre sus actos.'
  },
  {
    key: 'environment_control',
    title: 'CONTROL DEL PROPIO ENTORNO',
    text: 'Expresan interés por espacios, grupos, organizaciones e instancias en los cuales se decide sobre asuntos de interés colectivo. Muestran interés por espacios o grupos que buscan transformar o mejorar sus condiciones de vida y su entorno. Se reconocen como gestores de desarrollo de su territorio. Expresan interés y asumen corresponsabilidad con lo público.'
  },
  {
    key: 'imagination',
    title: 'IMAGINACIÓN',
    text: 'Manifiestan sus ideas con recursos estéticos y artísticos. Participan de manera activa en actividades que movilizan emociones y expresiones creativas. Vinculan los desarrollos formativos con su propia vida.'
  }
];

const PROBLEM_AXES = [
  { value: 'territorial_development', label: 'DESARROLLO TERRITORIAL' },
  { value: 'social_control', label: 'CONTROL SOCIAL' },
  {
    value: 'coexistence_conflict_resolution',
    label: 'CONVIVENCIA Y RESOLUCIÓN DE CONFLICTOS'
  },
  { value: 'inclusion', label: 'INCLUSIÓN' }
];

function emptyAnswers(items) {
  return Object.fromEntries(items.map(item => [item.key, '']));
}

function RequiredMark() {
  return <span className='territory-visit-form__required' aria-hidden='true'>*</span>;
}

const EMPTY_FORM = {
  sessionType: '',
  groupId: '',
  schoolId: '',
  municipalityId: '',
  sessionDate: '',
  startTime: '',
  endTime: '',
  mediatorId: '',
  observerPosition: '',
  methodologyAnswers: emptyAnswers(METHODOLOGY_QUESTIONS),
  methodologyComments: '',
  facilitatorCapacityAnswers: emptyAnswers(FACILITATOR_CAPACITIES),
  facilitatorComments: '',
  participantCapacityAnswers: emptyAnswers(PARTICIPANT_CAPACITIES),
  participantComments: '',
  problemAxes: [],
  problemAxesComments: ''
};

function YesNoMatrix({ title, items, answers, onChange }) {
  return (
    <fieldset className='territory-visit-matrix'>
      <legend>{title} <RequiredMark /></legend>
      <div className='territory-visit-matrix__header' aria-hidden='true'>
        <span></span>
        <span>Sí</span>
        <span>No</span>
      </div>
      {items.map(item => (
        <div className='territory-visit-matrix__row' key={item.key}>
          <div>
            {item.title && <strong>{item.title}:</strong>}
            <span>{item.text}</span>
          </div>
          <label aria-label={`${item.title || item.text}: Sí`}>
            <input
              required
              type='radio'
              name={`${title}-${item.key}`}
              value='yes'
              checked={answers[item.key] === 'yes'}
              onChange={() => onChange(item.key, 'yes')}
            />
          </label>
          <label aria-label={`${item.title || item.text}: No`}>
            <input
              required
              type='radio'
              name={`${title}-${item.key}`}
              value='no'
              checked={answers[item.key] === 'no'}
              onChange={() => onChange(item.key, 'no')}
            />
          </label>
        </div>
      ))}
    </fieldset>
  );
}

export default function TerritoryVisitForm() {
  const { userName } = useUserName();
  const [options, setOptions] = useState({
    groups: [],
    schools: [],
    municipalities: [],
    mediators: [],
    currentObserver: null
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    let active = true;
    getTerritoryVisitContext().then(data => {
      if (active && data) {
        setOptions(data);
      }
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  function updateField(field, value) {
    setForm(current => ({ ...current, [field]: value }));
  }

  function updateAnswer(section, key, value) {
    setForm(current => ({
      ...current,
      [section]: { ...current[section], [key]: value }
    }));
  }

  function toggleProblemAxis(axis) {
    setForm(current => ({
      ...current,
      problemAxes: current.problemAxes.includes(axis)
        ? current.problemAxes.filter(item => item !== axis)
        : [...current.problemAxes, axis]
    }));
  }

  function booleanAnswers(answers) {
    return Object.fromEntries(
      Object.entries(answers).map(([key, value]) => [key, value === 'yes'])
    );
  }

  function selectGroup(groupId) {
    const group = options.groups.find(item => Number(item.id) === Number(groupId));
    const school = options.schools.find(
      item => Number(item.id) === Number(group?.schoolId)
    );
    setForm(current => ({
      ...current,
      groupId,
      schoolId: group ? String(group.schoolId) : current.schoolId,
      mediatorId: group?.mediatorId ? String(group.mediatorId) : '',
      municipalityId: group?.municipalityId || school?.municipalityId
        ? String(group?.municipalityId || school?.municipalityId)
        : current.municipalityId
    }));
  }

  async function submit(event) {
    event.preventDefault();
    if (!form.groupId) {
      toast('Seleccione un código de grupo válido', { type: 'warning' });
      return;
    }
    if (form.endTime <= form.startTime) {
      toast('La hora final debe ser posterior a la hora de inicio', {
        type: 'warning'
      });
      return;
    }

    if (!form.problemAxes.length) {
      toast('Seleccione al menos un eje problematizador', { type: 'warning' });
      return;
    }
    if (photo && photo.size > 5 * 1024 * 1024) {
      toast('La fotografía no puede superar los 5 MB', { type: 'warning' });
      return;
    }

    setSubmitting(true);
    const saved = await createTerritoryVisit({
      ...form,
      methodologyAnswers: booleanAnswers(form.methodologyAnswers),
      facilitatorCapacityAnswers: booleanAnswers(
        form.facilitatorCapacityAnswers
      ),
      participantCapacityAnswers: booleanAnswers(
        form.participantCapacityAnswers
      ),
      photo
    });
    setSubmitting(false);
    if (!saved) return;

    toast('Visita registrada correctamente', { type: 'success' });
    setForm(EMPTY_FORM);
    setPhoto(null);
  }

  return (
    <section className='territory-visit-form-page'>
      <form className='territory-visit-form' onSubmit={submit}>
        <header>
          <h1>Registrar visita</h1>
          <p>Visitas a actividades en territorio</p>
        </header>

        {loading ? (
          <p className='territory-visit-form__status'>Cargando opciones...</p>
        ) : (
          <div className='territory-visit-form__grid'>
            <label>
              <span>Tipo de sesión <RequiredMark /></span>
              <select
                required
                value={form.sessionType}
                onChange={event => updateField('sessionType', event.target.value)}>
                <option value=''>Seleccione una opción</option>
                {SESSION_TYPES.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <div className='territory-visit-form__field'>
              <span>Código de grupo <RequiredMark /></span>
              <CustomFilterSelect
                optionsList={options.groups.map(group => ({
                  id: group.id,
                  name: group.code
                }))}
                placeholder='Seleccione un grupo'
                valueEdit={
                  options.groups.find(
                    group => Number(group.id) === Number(form.groupId)
                  )?.code || ''
                }
                addInfo={(_key, groupId) => selectGroup(String(groupId))}
                keyName='groupId'
                searchable
                searchPlaceholder='Buscar código de grupo...'
              />
            </div>

            <label>
              <span>Nombre de la Institución Educativa <RequiredMark /></span>
              <input
                readOnly
                type='text'
                value={
                  options.schools.find(
                    school => Number(school.id) === Number(form.schoolId)
                  )?.name || ''
                }
                placeholder='Se completa al seleccionar el grupo'
              />
            </label>

            <label>
              <span>Comuna/Corregimiento <RequiredMark /></span>
              <input
                readOnly
                type='text'
                value={
                  options.municipalities.find(
                    municipality =>
                      Number(municipality.id) === Number(form.municipalityId)
                  )?.name || ''
                }
                placeholder='Se completa al seleccionar el grupo'
              />
            </label>

            <label>
              <span>Fecha de realización <RequiredMark /></span>
              <input
                required
                type='date'
                value={form.sessionDate}
                onChange={event => updateField('sessionDate', event.target.value)}
              />
            </label>

            <label>
              <span>Hora de inicio <RequiredMark /></span>
              <input
                required
                type='time'
                value={form.startTime}
                onChange={event => updateField('startTime', event.target.value)}
              />
            </label>

            <label>
              <span>Hora final <RequiredMark /></span>
              <input
                required
                type='time'
                min={form.startTime || undefined}
                value={form.endTime}
                onChange={event => updateField('endTime', event.target.value)}
              />
            </label>

            <label>
              <span>Nombre del mediador <RequiredMark /></span>
              <input
                readOnly
                type='text'
                value={
                  form.mediatorId
                  ? options.mediators.find(
                      mediator =>
                        Number(mediator.id) === Number(form.mediatorId)
                    )?.fullName || ''
                  : ''
                }
                placeholder='Se completa al seleccionar el grupo'
              />
            </label>

            <label>
              <span>Nombre del observador <RequiredMark /></span>
              <input
                readOnly
                type='text'
                value={userName || options.currentObserver?.fullName || ''}
                placeholder='Administrador autenticado'
              />
            </label>

            <label>
              <span>Cargo del observador <RequiredMark /></span>
              <input
                required
                type='text'
                maxLength='180'
                placeholder='Ingrese el cargo'
                value={form.observerPosition}
                onChange={event =>
                  updateField('observerPosition', event.target.value)
                }
              />
            </label>
          </div>
        )}

        {!loading && (
          <div className='territory-visit-form__observations'>
            <YesNoMatrix
              title='Observación metodología de la actividad'
              items={METHODOLOGY_QUESTIONS}
              answers={form.methodologyAnswers}
              onChange={(key, value) =>
                updateAnswer('methodologyAnswers', key, value)
              }
            />

            <label className='territory-visit-form__comments'>
              <span>Comentarios</span>
              <textarea
                rows='4'
                value={form.methodologyComments}
                onChange={event =>
                  updateField('methodologyComments', event.target.value)
                }
                placeholder='Escriba sus comentarios'
              />
            </label>

            <section className='territory-visit-form__information'>
              <h2>
                CAPACIDADES SEGÚN EL SISTEMA DE FORMACIÓN CIUDADANA PARA LA
                PARTICIPACIÓN
              </h2>
              <p>
                De acuerdo con los observables de cada capacidad, responda SÍ o
                NO respecto a las capacidades que considera se logran evidenciar
                en la actividad, tanto desde el facilitador como desde el grupo
                participante. Elija SÍ cuando al menos uno sea evidenciado.
              </p>
              <p>
                En cuanto al grupo de participantes, elija cuando lo observado
                corresponda con una tendencia grupal, es decir, visto o expresado
                por un grupo significativo de participantes, por ejemplo un 40%.
              </p>
            </section>

            <YesNoMatrix
              title='Capacidades desde el facilitador'
              items={FACILITATOR_CAPACITIES}
              answers={form.facilitatorCapacityAnswers}
              onChange={(key, value) =>
                updateAnswer('facilitatorCapacityAnswers', key, value)
              }
            />

            <label className='territory-visit-form__comments'>
              <span>Comentarios sobre las capacidades desde el facilitador</span>
              <textarea
                rows='4'
                value={form.facilitatorComments}
                onChange={event =>
                  updateField('facilitatorComments', event.target.value)
                }
                placeholder='Escriba sus comentarios'
              />
            </label>

            <YesNoMatrix
              title='Capacidades desde el grupo participante'
              items={PARTICIPANT_CAPACITIES}
              answers={form.participantCapacityAnswers}
              onChange={(key, value) =>
                updateAnswer('participantCapacityAnswers', key, value)
              }
            />

            <label className='territory-visit-form__comments'>
              <span>
                Comentarios sobre las capacidades desde el grupo participante
              </span>
              <textarea
                rows='4'
                value={form.participantComments}
                onChange={event =>
                  updateField('participantComments', event.target.value)
                }
                placeholder='Escriba sus comentarios'
              />
            </label>

            <fieldset className='territory-visit-form__axes'>
              <legend>
                EJES PROBLEMATIZADORES SEGÚN EL SISTEMA DE FORMACIÓN CIUDADANA
                PARA LA PARTICIPACIÓN <RequiredMark />
              </legend>
              <p>
                ¿Cuál eje o ejes problematizadores tuvieron énfasis, se
                evidenciaron o fueron transversalizados en la actividad?
                Seleccione uno o varios.
              </p>
              <div>
                {PROBLEM_AXES.map(axis => (
                  <label key={axis.value}>
                    <input
                      type='checkbox'
                      checked={form.problemAxes.includes(axis.value)}
                      onChange={() => toggleProblemAxis(axis.value)}
                    />
                    <span>{axis.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label className='territory-visit-form__comments'>
              <span>Comentarios sobre los ejes problematizadores</span>
              <textarea
                rows='4'
                value={form.problemAxesComments}
                onChange={event =>
                  updateField('problemAxesComments', event.target.value)
                }
                placeholder='Escriba sus comentarios'
              />
            </label>

            <label className='territory-visit-form__photo'>
              <span>Registro fotográfico (opcional)</span>
              <input
                type='file'
                accept='image/jpeg,image/png,image/webp'
                onChange={event => setPhoto(event.target.files?.[0] || null)}
              />
              <small>Formatos JPG, PNG o WEBP. Tamaño máximo: 5 MB.</small>
            </label>
          </div>
        )}

        <button type='submit' disabled={loading || submitting}>
          {submitting ? 'Guardando...' : 'Registrar visita'}
        </button>
      </form>
    </section>
  );
}
